<script lang="ts">
    import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
    import { TRANSLATION_NAMESPACES } from "@apps/rewards/constants/constants";
    import {
        RewardsHubB2bWidgetDefinition,
        RewardsHubWidgetDefinition,
    } from "@apps/rewards/constants/definitions";
    import { BRANDING_LAUNCHER_STYLE } from "@apps/rewards/modules/StorefrontValues/constants";
    import type RewardsStorefrontValuesService from "@apps/rewards/modules/StorefrontValues/service/RewardsStorefrontValuesService";
    import { REWARDS_SDK_METHODS } from "@apps/rewards/rewards_sdk/constants";
    import type CloseRewardsPopupSdkMethodPayload from "@apps/rewards/rewards_sdk/methods/CloseRewardsPopupSdkMethodPayload";
    import type OpenRewardsPopupSdkMethodPayload from "@apps/rewards/rewards_sdk/methods/OpenRewardsPopupSdkMethodPayload";
    import RewardsDiResolverService from "@apps/rewards/services/RewardsDiResolverService/RewardsDIResolverService";
    import type { TranslationNamespaces } from "@apps/rewards/types";
    import { CustomerInfoProviderInterface } from "@lib/CustomerInfoProvider/CustomerInfoProviderInterface";
    import { OBSERVED_PARAMS } from "@modules/common_module/HashParamsProvider/constants";
    import HashParamsProvider from "@modules/common_module/HashParamsProvider/HashParamsProvider";
    import type { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import type { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import WidgetClosedSdkEvent from "@modules/sdk/common_sdk/events/WidgetClosedSdkEvent";
    import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
    import WidgetOpenedSdkEvent from "@modules/sdk/common_sdk/events/WidgetOpenedSdkEvent";
    import {
        dispatchSdkEvent,
        subscribeToSdkMethod,
    } from "@modules/sdk/helpers";
    import {
        FocusTrapReturnType,
        focusTrap,
    } from "@ui-utils/actions/focusTrap";
    import { Subscription, skip } from "rxjs";
    import { onMount } from "svelte";

    import DynamicImport from "@components/DynamicImport/DynamicImport.svelte";
    import Widget from "@components/Widget.svelte";
    import type { LoggerInterface } from "@interfaces/LoggerInterface";

    import RewardsPopUpAndDrawerWidgetTheme from "../../components/Theme/RewardsPopUpAndDrawerWidgetTheme.svelte";
    import RewardsPopUpWidgetContainer from "./components/RewardsPopUpWidgetContainer.svelte";
    import RewardsPopUpWidgetLauncherButton from "./components/RewardsPopUpWidgetLauncherButton.svelte";
    import type RewardsPopUpWidgetLauncherManager from "./utils/RewardsPopUpWidgetLauncherManager/RewardsPopUpWidgetLauncherManager";

    //Props
    export let storefrontValuesService: RewardsStorefrontValuesService;
    export let dynamicImportManager: DynamicImportManager;
    export let launcherManager: RewardsPopUpWidgetLauncherManager;
    export let i18n: GwI18n;
    export let hashParamsProvider: HashParamsProvider;
    export let logger: LoggerInterface;
    export let widgetName: REWARDS_WIDGET_NAMES;
    export let widgetNamespace: TranslationNamespaces;
    export let customerInfoProvider: CustomerInfoProviderInterface;
    export let afterWidgetInject: () => void;

    const branding$ = storefrontValuesService.selectRewardsBranding();
    const isPopUpWidgetOpen$ = launcherManager.isPopUpWidgetOpen$;
    const urlHashParams$ = hashParamsProvider.selectParams();
    const isCustomerB2b = customerInfoProvider.getIsB2b();
    let urlHashParamsSubscription: Subscription;
    const { textOnly, iconOnly } = BRANDING_LAUNCHER_STYLE;
    let focusTrapTargetRef: HTMLDivElement | null = null;
    let focusTrapInstance: FocusTrapReturnType;

    const openPopUpSubscription =
        subscribeToSdkMethod<OpenRewardsPopupSdkMethodPayload>(
            REWARDS_SDK_METHODS.OPEN_REWARDS_POPUP,
            ({ onSuccess }) => {
                launcherManager.rewardsPopUpWidgetShow();
                onSuccess();
            }
        );

    const closePopUpSubscription =
        subscribeToSdkMethod<CloseRewardsPopupSdkMethodPayload>(
            REWARDS_SDK_METHODS.CLOSE_REWARDS_POPUP,
            ({ onSuccess }) => {
                launcherManager.rewardsPopUpWidgetHide();
                onSuccess();
            }
        );

    const isPopUpWidgetOpenSubscription = isPopUpWidgetOpen$
        .pipe(skip(1))
        .subscribe((isOpen) => {
            if (isOpen) {
                dispatchSdkEvent(new WidgetOpenedSdkEvent(widgetName));
            } else {
                dispatchSdkEvent(new WidgetClosedSdkEvent(widgetName));
            }
        });

    const onElementMount = (target: HTMLElement) => {
        if (focusTrapTargetRef) {
            focusTrapInstance = focusTrap(focusTrapTargetRef, {
                enabled: true,
                elements: [target],
                options: {
                    initialFocus: false,
                    allowOutsideClick: true,
                    clickOutsideDeactivates: true,
                },
            });
        }
    };

    onMount(() => {
        afterWidgetInject();

        return () => {
            openPopUpSubscription.unsubscribe();
            closePopUpSubscription.unsubscribe();
            isPopUpWidgetOpenSubscription.unsubscribe();
            urlHashParamsSubscription?.unsubscribe();
        };
    });

    const handlePopupButtonLoadAction = () => {
        urlHashParamsSubscription = urlHashParams$.subscribe((params) => {
            if (
                params[OBSERVED_PARAMS.REWARDS_HOME] ||
                params[OBSERVED_PARAMS.GW_REWARDS_HOME] ||
                params[OBSERVED_PARAMS.GW_REWARDS_EARNING_RULES] ||
                params[OBSERVED_PARAMS.GW_REWARDS_SPENDING_RULES]
            ) {
                launcherManager.rewardsPopUpWidgetShow();
            }
        });
    };

    const handlePopupButtonRemoveAction = () => {
        urlHashParamsSubscription?.unsubscribe();
    };

    $: !$isPopUpWidgetOpen$ && focusTrapInstance?.destroy();
</script>

{#if $branding$}
    <RewardsPopUpAndDrawerWidgetTheme branding={$branding$}>
        {@const {
            position: { placement: widgetPosition },
            appearance: { style },
            icon,
        } = $branding$.popUpWidget.launcher}
        {#if isCustomerB2b}
            <DynamicImport
                let:value={{
                    module,
                    importModule,
                }}
                definition={RewardsHubB2bWidgetDefinition}
                {dynamicImportManager}
                shouldImportModule={$isPopUpWidgetOpen$}
            >
                <RewardsPopUpWidgetLauncherButton
                    isPopUpWidgetOpen={$isPopUpWidgetOpen$}
                    {i18n}
                    {icon}
                    hideIcon={style === textOnly}
                    hideText={style === iconOnly}
                    {widgetPosition}
                    {widgetNamespace}
                    on:popupButtonLoad={handlePopupButtonLoadAction}
                    on:popupButtonRemove={handlePopupButtonRemoveAction}
                    on:click={() => launcherManager.rewardsPopUpWidgetToggle()}
                    on:mouseover={importModule}
                />
                {#if $isPopUpWidgetOpen$ && module}
                    <RewardsPopUpWidgetContainer
                        {widgetPosition}
                        onClose={() => launcherManager.rewardsPopUpWidgetHide()}
                        bind:element={focusTrapTargetRef}
                    >
                        <Widget
                            class="gw-rd-popup-root-widget-wrapper"
                            widget={module}
                            params={[
                                {
                                    isWidgetShadowRootEnabled: false,
                                    onElementMount,
                                    widgetName, //Here widget name is "RewardsPopUpWidget" for backward compatibility with customizations
                                    translations: {
                                        widgetNamespace:
                                            TRANSLATION_NAMESPACES.hubWidget,
                                        criticalWidgetNamespace:
                                            TRANSLATION_NAMESPACES.criticalPopUpWidget,
                                        pointsNamespace:
                                            TRANSLATION_NAMESPACES.commonCriticalPoints,
                                    },
                                    diResolverService:
                                        new RewardsDiResolverService(),
                                },
                                logger,
                            ]}
                            on:widgetInit={() =>
                                dispatchSdkEvent(
                                    new WidgetInjectedSdkEvent(
                                        REWARDS_WIDGET_NAMES.REWARDS_HUB_WIDGET
                                    )
                                )}
                        />
                    </RewardsPopUpWidgetContainer>
                {/if}
            </DynamicImport>
        {:else}
            <DynamicImport
                let:value={{
                    module,
                    importModule,
                }}
                definition={RewardsHubWidgetDefinition}
                {dynamicImportManager}
                shouldImportModule={$isPopUpWidgetOpen$}
            >
                <RewardsPopUpWidgetLauncherButton
                    isPopUpWidgetOpen={$isPopUpWidgetOpen$}
                    {i18n}
                    {icon}
                    hideIcon={style === textOnly}
                    hideText={style === iconOnly}
                    {widgetPosition}
                    {widgetNamespace}
                    on:popupButtonLoad={handlePopupButtonLoadAction}
                    on:popupButtonRemove={handlePopupButtonRemoveAction}
                    on:click={() => launcherManager.rewardsPopUpWidgetToggle()}
                    on:mouseover={importModule}
                />
                {#if $isPopUpWidgetOpen$ && module}
                    <RewardsPopUpWidgetContainer
                        {widgetPosition}
                        onClose={() => launcherManager.rewardsPopUpWidgetHide()}
                        bind:element={focusTrapTargetRef}
                    >
                        <Widget
                            class="gw-rd-popup-root-widget-wrapper"
                            widget={module}
                            params={[
                                {
                                    isWidgetShadowRootEnabled: null,
                                    onElementMount,
                                    widgetName, //Here widget name is "RewardsPopUpWidget" for backward compatibility with customizations
                                    translations: {
                                        widgetNamespace:
                                            TRANSLATION_NAMESPACES.hubWidget,
                                        criticalWidgetNamespace:
                                            TRANSLATION_NAMESPACES.criticalPopUpWidget,
                                        pointsNamespace:
                                            TRANSLATION_NAMESPACES.commonCriticalPoints,
                                    },
                                    diResolverService:
                                        new RewardsDiResolverService(),
                                },
                                logger,
                            ]}
                            on:widgetInit={() =>
                                dispatchSdkEvent(
                                    new WidgetInjectedSdkEvent(
                                        REWARDS_WIDGET_NAMES.REWARDS_HUB_WIDGET
                                    )
                                )}
                        />
                    </RewardsPopUpWidgetContainer>
                {/if}
            </DynamicImport>
        {/if}
    </RewardsPopUpAndDrawerWidgetTheme>
{/if}

<style lang="postcss">
    :global(.gw-rd-popup-root-widget-wrapper) {
        height: 100%;
    }
</style>
